import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * AnimatedRobot — an SVG robot character that:
 * - Floats up and down
 * - Antenna LED pulses
 * - Eyes blink periodically
 * - Mouth animates while "talking"
 * - Arms slightly bob (typing on laptop)
 * - Speech bubble cycles through marketing taglines
 * - Floating social icons orbit around the figure
 * - Glowing neon podium beneath
 *
 * Pure SVG + CSS/framer-motion. No external assets.
 */

const TALK_MESSAGES = [
  "I write your content while you sleep ✦",
  "I rank you on Google in 30 days 🚀",
  "I reply to every Instagram DM 💬",
  "I run your Meta ads — no agency needed 🎯",
  "I never miss a campaign deadline ⚡",
];

export default function AnimatedRobot() {
  const [messageIdx, setMessageIdx] = useState(0);
  const [talking, setTalking] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setMessageIdx((i) => (i + 1) % TALK_MESSAGES.length);
    }, 3800);
    return () => clearInterval(t);
  }, []);

  // Toggle talking quickly to drive mouth animation
  useEffect(() => {
    const t = setInterval(() => setTalking((v) => !v), 220);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-full aspect-square max-w-[560px] mx-auto select-none">
      {/* Ambient glow */}
      <div className="absolute inset-0 rounded-full bg-gradient-radial from-fuchsia-500/30 via-indigo-500/10 to-transparent blur-3xl" />

      {/* Orbiting social icons */}
      <OrbitingIcons />

      {/* Speech bubble */}
      <AnimatePresence mode="wait">
        <motion.div
          key={messageIdx}
          initial={{ opacity: 0, y: -10, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.9 }}
          transition={{ duration: 0.35 }}
          className="absolute top-[4%] left-[6%] z-20"
          data-testid="robot-speech-bubble"
        >
          <div className="relative">
            <div className="px-4 py-2.5 rounded-2xl bg-white/95 text-[#1c0f3f] text-sm font-semibold shadow-2xl shadow-fuchsia-500/30 max-w-[220px]">
              {TALK_MESSAGES[messageIdx]}
            </div>
            <svg className="absolute -bottom-2 left-6" width="20" height="14" viewBox="0 0 20 14" fill="none">
              <path d="M0 0 L20 0 L10 14 Z" fill="rgba(255,255,255,0.95)" />
            </svg>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Floating robot */}
      <motion.div
        className="absolute inset-0 flex items-end justify-center"
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <RobotSVG talking={talking} />
      </motion.div>

      {/* Neon podium */}
      <div className="absolute bottom-[2%] left-1/2 -translate-x-1/2 w-[78%] pointer-events-none">
        <div className="relative h-12">
          <div className="absolute inset-x-4 top-2 h-9 rounded-[50%] bg-gradient-to-b from-cyan-300/80 via-violet-500/60 to-transparent blur-md" />
          <div className="absolute inset-x-10 top-3 h-7 rounded-[50%] border-2 border-cyan-300/80 shadow-[0_0_30px_8px_rgba(56,189,248,0.55)]" />
          <div className="absolute inset-x-16 top-5 h-5 rounded-[50%] border border-fuchsia-400/70 shadow-[0_0_24px_4px_rgba(217,70,239,0.45)]" />
        </div>
      </div>
    </div>
  );
}

function OrbitingIcons() {
  // Each icon orbits around the centre of the robot at its own radius, speed,
  // and direction. The outer wrapper rotates; the inner counter-rotates so
  // icons stay upright while travelling on a circular path.
  const ICONS = [
    { Icon: IconFB,       color: "#1877F2", radius: 220, duration: 18, direction:  1 },
    { Icon: IconIG,       color: "white",   radius: 200, duration: 22, direction: -1 },
    { Icon: IconG,        color: "white",   radius: 230, duration: 20, direction:  1 },
    { Icon: IconLinkedIn, color: "#0A66C2", radius: 210, duration: 24, direction: -1 },
    { Icon: IconX,        color: "#000",    radius: 225, duration: 19, direction:  1 },
    { Icon: IconYouTube,  color: "#FF0000", radius: 205, duration: 21, direction: -1 },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none">
      {ICONS.map(({ Icon, color, radius, duration, direction }, idx) => {
        const startAngle = (idx * 360) / ICONS.length;
        const endAngle = startAngle + 360 * direction;
        return (
          <motion.div
            key={idx}
            className="absolute top-1/2 left-1/2"
            style={{ width: 0, height: 0 }}
            initial={{ rotate: startAngle }}
            animate={{ rotate: endAngle }}
            transition={{ duration, repeat: Infinity, ease: "linear", repeatType: "loop" }}
          >
            {/* Static translate wrapper — pushes the icon to its orbital radius.
                Must NOT be a motion component, otherwise framer overwrites transform. */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                transform: `translate(-50%, -50%) translateY(-${radius}px)`,
              }}
            >
              {/* Counter-rotator so the icon stays upright while it orbits */}
              <motion.div
                initial={{ rotate: -startAngle }}
                animate={{ rotate: -endAngle }}
                transition={{ duration, repeat: Infinity, ease: "linear", repeatType: "loop" }}
              >
                <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl shadow-xl shadow-black/40 grid place-items-center bg-white ring-2 ring-white/40">
                  <Icon className="h-7 w-7" color={color} />
                </div>
              </motion.div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ----------------------- Robot SVG ----------------------- */
function RobotSVG({ talking }) {
  return (
    <svg viewBox="0 0 380 460" className="w-[78%] h-auto drop-shadow-[0_18px_30px_rgba(99,102,241,0.45)]" data-testid="ai-robot">
      <defs>
        <linearGradient id="bodyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#D5C9F2" />
        </linearGradient>
        <linearGradient id="bodyGradAccent" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A78BFA" />
          <stop offset="100%" stopColor="#5B21B6" />
        </linearGradient>
        <linearGradient id="faceGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1E0E4A" />
          <stop offset="100%" stopColor="#3A1B7A" />
        </linearGradient>
        <linearGradient id="screenGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#5b21b6" />
        </linearGradient>
        <linearGradient id="ig-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f9ce34" />
          <stop offset="50%" stopColor="#ee2a7b" />
          <stop offset="100%" stopColor="#6228d7" />
        </linearGradient>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      {/* Antenna */}
      <g>
        <line x1="190" y1="78" x2="190" y2="45" stroke="#C4B5FD" strokeWidth="3" strokeLinecap="round" />
        <motion.circle
          cx="190" cy="40" r="7" fill="#22D3EE"
          animate={{ r: [6, 9, 6], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        />
        <circle cx="190" cy="40" r="14" fill="#22D3EE" opacity="0.25" filter="url(#glow)" />
      </g>

      {/* Head */}
      <g>
        {/* Outer head */}
        <rect x="118" y="78" width="144" height="120" rx="42" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        {/* Ears */}
        <rect x="108" y="118" width="14" height="40" rx="6" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        <rect x="258" y="118" width="14" height="40" rx="6" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        <circle cx="115" cy="138" r="3" fill="#A78BFA" />
        <circle cx="265" cy="138" r="3" fill="#A78BFA" />

        {/* Face screen */}
        <rect x="138" y="100" width="104" height="76" rx="30" fill="url(#faceGrad)" />
        {/* Visor highlight */}
        <rect x="140" y="102" width="100" height="20" rx="20" fill="white" opacity="0.05" />

        {/* Eyes (blinking) */}
        <motion.g
          animate={{ scaleY: [1, 1, 0.1, 1, 1] }}
          transition={{ duration: 4, repeat: Infinity, times: [0, 0.92, 0.95, 0.98, 1] }}
          style={{ transformOrigin: "190px 138px", transformBox: "fill-box" }}
        >
          <ellipse cx="166" cy="138" rx="10" ry="11" fill="#22D3EE" />
          <ellipse cx="214" cy="138" rx="10" ry="11" fill="#22D3EE" />
          {/* Eye highlights */}
          <ellipse cx="170" cy="134" rx="3" ry="3.5" fill="#fff" opacity="0.9" />
          <ellipse cx="218" cy="134" rx="3" ry="3.5" fill="#fff" opacity="0.9" />
        </motion.g>

        {/* Smile / mouth — animated talking */}
        <motion.path
          d="M170 162 Q190 178 210 162"
          stroke="#22D3EE"
          strokeWidth="4"
          strokeLinecap="round"
          fill="transparent"
          animate={{
            d: talking
              ? "M170 162 Q190 174 210 162"
              : "M170 162 Q190 168 210 162",
          }}
          transition={{ duration: 0.2 }}
        />
        {/* Cheek blush dots */}
        <circle cx="148" cy="160" r="4" fill="#F472B6" opacity="0.7" />
        <circle cx="232" cy="160" r="4" fill="#F472B6" opacity="0.7" />
      </g>

      {/* Neck */}
      <rect x="178" y="196" width="24" height="14" rx="4" fill="#C4B5FD" />

      {/* Body / torso */}
      <g>
        <rect x="130" y="208" width="120" height="100" rx="28" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        {/* Chest panel */}
        <rect x="150" y="226" width="80" height="58" rx="12" fill="url(#bodyGradAccent)" opacity="0.85" />
        {/* Chest LED dots */}
        <circle cx="170" cy="252" r="3" fill="#22D3EE" />
        <motion.circle
          cx="190" cy="252" r="3" fill="#F472B6"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        />
        <circle cx="210" cy="252" r="3" fill="#A78BFA" />
      </g>

      {/* Arms — animated bob (typing) */}
      <motion.g
        animate={{ y: [0, -2, 0] }}
        transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* Left upper arm */}
        <rect x="100" y="220" width="32" height="56" rx="14" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        {/* Left forearm */}
        <rect x="98" y="272" width="34" height="52" rx="14" fill="url(#bodyGradAccent)" />
        {/* Left hand */}
        <circle cx="115" cy="330" r="14" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />

        {/* Right upper arm */}
        <rect x="248" y="220" width="32" height="56" rx="14" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
        <rect x="248" y="272" width="34" height="52" rx="14" fill="url(#bodyGradAccent)" />
        <circle cx="265" cy="330" r="14" fill="url(#bodyGrad)" stroke="#6D28D9" strokeWidth="2" />
      </motion.g>

      {/* Laptop */}
      <g>
        {/* Screen */}
        <rect x="108" y="312" width="164" height="74" rx="8" fill="url(#screenGrad)" stroke="#1E0E4A" strokeWidth="3" />
        {/* M logo on screen */}
        <text
          x="190" y="362" textAnchor="middle"
          fontFamily="Cabinet Grotesk, sans-serif" fontWeight="900" fontSize="46" fill="#fff"
        >
          M
        </text>
        {/* Screen reflection */}
        <rect x="112" y="316" width="156" height="14" rx="6" fill="white" opacity="0.08" />
        {/* Base */}
        <path d="M96 386 L284 386 L296 402 L84 402 Z" fill="#312E81" stroke="#1E0E4A" strokeWidth="2" />
        <rect x="170" y="392" width="40" height="4" rx="2" fill="#22D3EE" opacity="0.6" />
      </g>

      {/* Body legs/footer (hidden under podium glow) */}
      <rect x="160" y="404" width="60" height="14" rx="6" fill="#312E81" opacity="0.5" />
    </svg>
  );
}

/* ----------------------- Social Icons (inline SVG) ----------------------- */
function IconFB({ className, color }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill={color} d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-3h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5V12h2.6l-.4 3h-2.2v7A10 10 0 0 0 22 12z" />
    </svg>
  );
}
function IconIG({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="3" y="3" width="18" height="18" rx="5" fill="url(#ig-grad)" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="white" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
    </svg>
  );
}
function IconG({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22 12.2c0-.7-.1-1.5-.2-2.1H12v4.1h5.6c-.2 1.3-1 2.4-2 3.2v2.6h3.3c2-1.8 3.1-4.5 3.1-7.8z" />
      <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.6c-.9.6-2.1 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3v2.6A10 10 0 0 0 12 22z" />
      <path fill="#FBBC05" d="M6.4 13.9c-.2-.6-.3-1.3-.3-1.9s.1-1.3.3-1.9V7.5H3a10 10 0 0 0 0 9z" />
      <path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9C16.9 2.8 14.7 2 12 2A10 10 0 0 0 3 7.5L6.4 10c.8-2.3 3-4.1 5.6-4.1z" />
    </svg>
  );
}
function IconLinkedIn({ className, color }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="2" y="2" width="20" height="20" rx="3" fill={color} />
      <path fill="white" d="M6.5 9h2.5v9H6.5zM7.7 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM10.5 9h2.4v1.3c.5-.9 1.5-1.5 2.9-1.5 2.4 0 2.9 1.5 2.9 3.7V18h-2.5v-4.6c0-1.1-.4-1.9-1.4-1.9-1.1 0-1.7.7-1.7 1.9V18h-2.5z" />
    </svg>
  );
}
function IconX({ className, color }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="2" y="2" width="20" height="20" rx="3" fill={color} />
      <path fill="white" d="M16.7 6h2L13.2 12 19.7 18h-4.6l-3.5-4.2L7.7 18H5.5l5.7-6.4L5 6h4.7l3.2 3.8z" />
    </svg>
  );
}
function IconYouTube({ className, color }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="2" y="5" width="20" height="14" rx="4" fill={color} />
      <path fill="white" d="M10 9.2v5.6L15 12z" />
    </svg>
  );
}
